import {
  useGetJobFormOptionsQuery,
  useGetJobQuery,
  useGetDispatcherByUserIdQuery,
  ListDispatchersAutocompleteDocument,
  Dtos_V1_ExtraSystemDto
} from '@/common/infrastructure/gql/generated/schema';
import { useContext, useMemo, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';

import { filterEquipmentSizes, filterEquipmentSizesByActiveEquipment } from '@/jobs/application/utils/equipmentSizeUtils';
import { mapJobToFormData, CurrentUserInfo } from '../mappers/JobMappers';
import { JobDetails } from '../types/JobTypes';
import AuthContext from '@/auth/application/AuthContext';

export const useGetJob = (jobId?: number, skipQuery?: boolean) => {
  const skip = skipQuery || !jobId || Number.isNaN(jobId);
  const authContext = useContext(AuthContext);

  const { data, loading, error, refetch } = useGetJobQuery({
    variables: { jobId: jobId! },
    skip,
    fetchPolicy: 'cache-and-network',
    returnPartialData: true,
    notifyOnNetworkStatusChange: false
  });

  const {
    data: formOptionsData,
    loading: formOptionsLoading,
    error: formOptionsError
  } = useGetJobFormOptionsQuery({
    skip: skipQuery ?? false,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first'
  });

  // Get current user info from auth context to auto-populate dispatcher
  const currentUserId = authContext?.user?.userId ? Number(authContext.user.userId) : null;
  const currentUser: CurrentUserInfo | null =
    authContext?.user && currentUserId && currentUserId > 0
      ? {
          id: currentUserId,
          name: authContext.user.fullName || authContext.user.userName || ''
        }
      : null;

  // Query dispatcher directly by userId
  // Note: Backend filter might not work correctly, so we validate the result
  const { data: dispatcherByUserIdData, loading: dispatcherByUserIdLoading } = useGetDispatcherByUserIdQuery({
    variables: { userId: currentUserId! },
    skip: (skipQuery ?? false) || !currentUserId || currentUserId <= 0,
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  });

  // Fallback: If direct query fails or returns invalid result, search in full paginated list

  const shouldSearchFullList =
    !skipQuery &&
    currentUserId &&
    currentUserId > 0 &&
    (!dispatcherByUserIdData?.dispatchers?.items?.[0] || dispatcherByUserIdData.dispatchers.items[0].user?.userId !== currentUserId);

  // Use lazy query to fetch all pages sequentially until we find the dispatcher
  const [fetchDispatchersPage] = useLazyQuery(ListDispatchersAutocompleteDocument, {
    fetchPolicy: 'network-only'
  });

  const [foundDispatcherId, setFoundDispatcherId] = useState<number | null>(null);
  const [hasSearchedAllPages, setHasSearchedAllPages] = useState(false);

  // Search through all pages when needed
  useEffect(() => {
    if (!shouldSearchFullList || hasSearchedAllPages || foundDispatcherId) {
      return;
    }

    const abortController = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;

    const searchPage = async (skip: number) => {
      // Check if cancelled before proceeding
      if (abortController.signal.aborted) {
        return;
      }

      try {
        const result = await fetchDispatchersPage({
          variables: { skip, search: '' }
        });

        // Check if cancelled after async operation
        if (abortController.signal.aborted) {
          return;
        }

        const items = result.data?.dispatchers?.items || [];
        const totalCount = result.data?.dispatchers?.totalCount || 0;

        // Check if we found the dispatcher in this page
        const found = items.find((d: { user?: { userId?: number | null } | null }) => d.user?.userId === currentUserId);
        if (found) {
          // Check cancellation before state update
          if (!abortController.signal.aborted) {
            setFoundDispatcherId(found.id ?? null);
          }
          return;
        }

        // Check if there are more pages to search
        const nextSkip = skip + 100;
        if (nextSkip < totalCount) {
          // Continue searching next page
          timeoutId = setTimeout(() => {
            if (!abortController.signal.aborted) {
              searchPage(nextSkip);
            }
          }, 100); // Small delay to avoid overwhelming the server
        } else if (!abortController.signal.aborted) {
          // Check cancellation before state update
          setHasSearchedAllPages(true);
        }
      } catch (error) {
        // Check cancellation before state update
        if (!abortController.signal.aborted) {
          console.error('Error fetching dispatchers page:', error);
          setHasSearchedAllPages(true);
        }
      }
    };

    // Start searching from page 0
    searchPage(0);

    // Cleanup function to prevent state updates after unmount
    return () => {
      abortController.abort();
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [shouldSearchFullList, currentUserId, fetchDispatchersPage, hasSearchedAllPages, foundDispatcherId]);

  // Reset state when conditions change
  useEffect(() => {
    if (!shouldSearchFullList) {
      setFoundDispatcherId(null);
      setHasSearchedAllPages(false);
    }
  }, [shouldSearchFullList]);

  const job = data?.raptorJobDetails?.value as JobDetails | null | undefined;

  const formOptions = formOptionsData?.raptorJobSupportingDataQuery?.value;

  // Get dispatcher ID from the direct query result or fallback to paginated search
  // Backend might return dispatchers with user: null, so we must validate
  const dispatcherIdForAutoPopulate = useMemo(() => {
    if (!currentUser?.id) {
      return null;
    }

    // First, try the direct query result
    const directQueryDispatcher = dispatcherByUserIdData?.dispatchers?.items?.[0];
    if (directQueryDispatcher?.user?.userId === currentUser.id) {
      return directQueryDispatcher.id ?? null;
    }

    // Fallback: use found dispatcher from paginated search
    if (foundDispatcherId) {
      return foundDispatcherId;
    }

    // If no valid dispatcher found, return null
    return null;
  }, [dispatcherByUserIdData?.dispatchers?.items, foundDispatcherId, currentUser?.id]);

  // Only pass currentUser with dispatcherId if we found a matching dispatcher
  // Otherwise, pass null to avoid using userId as dispatcherId
  const userForMapper: CurrentUserInfo | null =
    currentUser && dispatcherIdForAutoPopulate ? { ...currentUser, id: dispatcherIdForAutoPopulate } : null;

  const formData = mapJobToFormData(job, formOptions?.availableExtraSystems as Dtos_V1_ExtraSystemDto[], userForMapper);

  return {
    job: formData,
    rawJob: job,
    loading: loading || formOptionsLoading || dispatcherByUserIdLoading,
    error: error || formOptionsError,
    cancelable: job?.cancelable,
    refetch,
    availableCrewMembers: formOptions?.availableCrewMembers ?? [],
    availableCrewRoles: formOptions?.availableCrewRoles ?? [],
    availableCustomers: formOptions?.availableCustomers ?? [],
    availableDispatchLocations: formOptions?.availableDispatchLocations ?? [],
    availableDispatchers: formOptions?.availableDispatchers ?? [],
    availableEquipment: formOptions?.availableEquipment ?? [],
    availableEquipmentLocations: formOptions?.availableEquipmentLocations ?? [],
    availableEquipmentSizes: (() => {
      const allSizes = formOptions?.availableEquipmentSizes ?? [];
      const equipment = formOptions?.availableEquipment ?? [];
      const deduplicatedSizes = filterEquipmentSizes(allSizes);
      return filterEquipmentSizesByActiveEquipment(deduplicatedSizes, equipment);
    })(),
    availableEquipmentTypes: formOptions?.availableEquipmentTypes ?? [],
    availableExtraSystems: formOptions?.availableExtraSystems ?? [],
    availableJobInformation: formOptions?.availableJobInformation ?? [],
    availableJobStatuses: formOptions?.availableJobStatuses ?? [],
    availableJobTypes: formOptions?.availableJobTypes ?? []
  };
};
